import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from "react";
import { Box, Container, Flex, Spinner, Text, IconButton, Collapse, Image, } from "@chakra-ui/react";
import { ChevronDownIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { usePubEventsByDate } from "../../hooks/usePubEvents";
import { useLocation, useNavigate, useParams, useSearchParams, } from "react-router-dom";
import DateSelectionCalendar from "../venue/DateSelectionCalendar";
import { removeAllOtherPartnerId, removeAllOtherRefs, } from "../../shared-utils/utils";
import EventDetailCard from "../event/EventDetailCard";
import EventDetails2 from "../../pages/EventDetails2";
import { IMG_URL } from "../common/Constant";
import { usePubConfig } from "../../hooks";
import { menuTypeTheme } from "../../shared-utils/values";
import { isLightColor } from "../common/util";
import { useTheme } from "@chakra-ui/react";
export default function PubEvents({ appContext = "customer", } = {}) {
    var _a, _b, _c, _d, _e, _f, _g;
    const { pubId: pub_id, bookingType } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Format date as YYYY-MM-DD for API
    const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    // Parse date from YYYY-MM-DD string
    const parseDateFromString = (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime()))
            return null;
        date.setHours(0, 0, 0, 0);
        return date;
    };
    // Initialize selectedDate from URL or default to today
    const getInitialDate = () => {
        const dateParam = searchParams.get("date");
        if (dateParam) {
            const parsedDate = parseDateFromString(dateParam);
            if (parsedDate)
                return parsedDate;
        }
        return new Date();
    };
    const [selectedDate, setSelectedDate] = useState(getInitialDate());
    const [showCalendar, setShowCalendar] = useState(false);
    const scrollContainerRef = useRef(null);
    const eventsContainerRef = useRef(null);
    const todayRef = useRef(null);
    const dateRefs = useRef({});
    // Get formatted date string for API
    const selectedDateString = formatDateForAPI(selectedDate);
    const { data: events, isLoading: isEventsLoading } = usePubEventsByDate(pub_id, selectedDateString);
    const { data: pubConfig, isLoading: pubConfigLoading } = usePubConfig(pub_id);
    const theme = useTheme();
    const defaultColors = theme.colors["dark"];
    const [pubColors, setPubColors] = useState(defaultColors);
    const idParam = searchParams.get("id");
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const ref = searchParams.get("ref");
        const partnerId = searchParams.get("refId");
        if (partnerId) {
            removeAllOtherPartnerId();
            localStorage.setItem(`z_partnerId_${pub_id}`, partnerId);
        }
        if (ref) {
            removeAllOtherRefs();
            localStorage.setItem(`z_ref_${pub_id}`, ref);
        }
    }, [location]);
    // Sync selectedDate with URL query param when URL changes
    useEffect(() => {
        const dateParam = searchParams.get("date");
        if (dateParam) {
            const parsedDate = parseDateFromString(dateParam);
            if (parsedDate) {
                // Only update if different to avoid unnecessary re-renders
                const currentDateString = formatDateForAPI(selectedDate);
                if (dateParam !== currentDateString) {
                    setSelectedDate(parsedDate);
                }
            }
        }
        else {
            // If no date param, set to today if current date is not today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const currentDateString = formatDateForAPI(selectedDate);
            const todayString = formatDateForAPI(today);
            if (currentDateString !== todayString) {
                setSelectedDate(today);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    // Generate dates for the next 30 days
    const generateDates = () => {
        const dates = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        for (let i = 0; i < 30; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            dates.push(date);
        }
        return dates;
    };
    const dates = generateDates();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const formatDayName = (date) => {
        return date.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
    };
    const formatMonth = (date) => {
        return date.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    };
    const isToday = (date) => {
        return (date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear());
    };
    const isSelected = (date) => {
        return (date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear());
    };
    const handleDateClick = (date) => {
        setSelectedDate(date);
        setShowCalendar(false);
        // Update URL query param with replace: true
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("date", formatDateForAPI(date));
        newSearchParams.delete("id");
        setSearchParams(newSearchParams, { replace: true });
    };
    const handleCalendarDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setShowCalendar(false); // Collapse calendar when date is selected
            // Update URL query param with replace: true
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set("date", formatDateForAPI(date));
            newSearchParams.delete("id");
            setSearchParams(newSearchParams, { replace: true });
        }
    };
    useEffect(() => {
        var _a;
        if (pubConfig === null || pubConfig === void 0 ? void 0 : pubConfig.data) {
            const { menuType, menuColors } = pubConfig.data;
            const themeKey = ((_a = menuTypeTheme[menuType]) === null || _a === void 0 ? void 0 : _a.theme) || "light";
            const fallbackTheme = theme.colors[themeKey];
            const menuTheme = menuTypeTheme[menuType] || {};
            const colors = {
                primary: (menuColors === null || menuColors === void 0 ? void 0 : menuColors.secondaryContainer) &&
                    menuColors.secondaryContainer !== "#000000"
                    ? menuColors.secondaryContainer
                    : menuTheme.primary || fallbackTheme.primary,
                secondary: (menuColors === null || menuColors === void 0 ? void 0 : menuColors.onPrimaryContainer) &&
                    menuColors.onPrimaryContainer !== "#000000"
                    ? menuColors.onPrimaryContainer
                    : menuTheme.secondary || fallbackTheme.secondary,
                bg: (menuColors === null || menuColors === void 0 ? void 0 : menuColors.onSecondaryContainer) &&
                    menuColors.onSecondaryContainer !== "#000000"
                    ? menuColors.onSecondaryContainer
                    : menuTheme.bg || fallbackTheme.bg,
                card_bg: (menuColors === null || menuColors === void 0 ? void 0 : menuColors.menuItemCard) && menuColors.menuItemCard !== "#000000"
                    ? menuColors.menuItemCard
                    : menuTheme.secondary || fallbackTheme.card_bg,
                text: (menuColors === null || menuColors === void 0 ? void 0 : menuColors.text)
                    ? menuColors.text
                    : menuTheme.text || fallbackTheme.text,
                icon_color: isLightColor((menuColors === null || menuColors === void 0 ? void 0 : menuColors.onSecondaryContainer) &&
                    menuColors.onSecondaryContainer !== "#000000"
                    ? menuColors.onSecondaryContainer
                    : menuTheme.bg || fallbackTheme.icon_color)
                    ? "black"
                    : "white",
                card_border: "#3A3D43",
            };
            setPubColors({
                ...fallbackTheme,
                ...colors,
            });
        }
    }, [pubConfig, theme]);
    useEffect(() => {
        // Scroll to selected date when it changes (only if not in view)
        if (scrollContainerRef.current && selectedDate) {
            const dateKey = formatDateForAPI(selectedDate);
            const dateElement = dateRefs.current[dateKey];
            if (dateElement) {
                const scrollContainer = scrollContainerRef.current;
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = dateElement.getBoundingClientRect();
                // Check if element is already visible in the container
                const isVisible = elementRect.left >= containerRect.left &&
                    elementRect.right <= containerRect.right;
                // Only scroll if not in view
                if (!isVisible) {
                    dateElement.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                        inline: "center",
                    });
                }
            }
        }
        // Scroll events container to top when date changes
        if (eventsContainerRef.current) {
            eventsContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [selectedDate]);
    const goBack = () => {
        if (!window.history || window.history.length <= 1) {
            navigate("/home");
        }
        else {
            navigate(-1);
        }
    };
    const filteredEvents = ((_b = (_a = events === null || events === void 0 ? void 0 : events.data) === null || _a === void 0 ? void 0 : _a.events) === null || _b === void 0 ? void 0 : _b.filter((event) => {
        if (event === null || event === void 0 ? void 0 : event.is_day_event)
            return false;
        if (!(event === null || event === void 0 ? void 0 : event.event_start_date))
            return true;
        const start = new Date(event.event_start_date);
        if (isNaN(start.getTime()))
            return true;
        const eventStartDate = new Date(start);
        eventStartDate.setHours(0, 0, 0, 0);
        // Include only non-day events whose start date is today
        return (eventStartDate.getDate() === getInitialDate().getDate() &&
            eventStartDate.getMonth() === getInitialDate().getMonth() &&
            eventStartDate.getFullYear() === getInitialDate().getFullYear());
    })) || [];
    return (_jsxs(Container, { maxW: "100vw", minH: "100vh", background: pubColors.bg || "#111111", p: "0", children: [_jsxs(Box, { w: "100%", bg: pubColors.bg || "#16171C", borderBottomRadius: "20px", borderBottom: `1px solid ${pubColors.card_border || "#2B2B2B"}`, py: 3, pl: 2, position: "fixed", top: 0, zIndex: 10, children: [_jsxs(Flex, { align: "center", gap: 0, position: "relative", overflow: "visible", children: [_jsx(IconButton, { "aria-label": "Scroll left", icon: _jsx(ArrowBackIcon, { boxSize: 5 }), size: "md", bg: "transparent", color: pubColors.text || "white", _hover: { bg: "rgba(255, 255, 255, 0.1)" }, onClick: goBack, flexShrink: 0, minW: "40px", h: "40px", borderRadius: "8px" }), _jsx(Box, { ref: scrollContainerRef, overflowX: "auto", overflowY: "hidden", flex: 1, css: {
                                    "&::-webkit-scrollbar": {
                                        display: "none",
                                    },
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                }, position: "relative", ml: 0, children: _jsx(Flex, { gap: 2, w: "max-content", position: "relative", pl: 0, style: { isolation: "isolate" }, children: dates.map((date, index) => {
                                        const dayName = formatDayName(date);
                                        const dayNum = date.getDate();
                                        const month = formatMonth(date);
                                        const isTodayDate = isToday(date);
                                        const isSelectedDate = isSelected(date);
                                        const dateKey = formatDateForAPI(date);
                                        return (_jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "center", bg: pubColors.bg || "#16171C", border: "none", h: "fit-content", position: isTodayDate ? "sticky" : "static", left: isTodayDate ? "-1px" : "auto", zIndex: isTodayDate ? 10 : 1, py: 1, children: [_jsxs(Box, { ref: (el) => {
                                                        if (isTodayDate && el) {
                                                            todayRef.current = el;
                                                        }
                                                        dateRefs.current[dateKey] = el;
                                                    }, flexShrink: 0, w: "56px", minW: "56px", h: "66px", borderRadius: isSelectedDate ? "8px" : isTodayDate ? "0" : "8px", bg: isSelectedDate
                                                        ? pubColors.primary || "#8155FE"
                                                        : isTodayDate
                                                            ? pubColors.bg || "#16171C"
                                                            : pubColors.card_bg || "#202020", border: isSelectedDate || isTodayDate
                                                        ? "none"
                                                        : `1px solid ${pubColors.card_border || "#2B2B2B"}`, display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", onClick: () => handleDateClick(date), children: [_jsx(Text, { fontSize: "10px", fontWeight: "400", color: isSelectedDate ? "white" : pubColors.text || "white", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", children: dayName }), _jsx(Text, { fontSize: "sm", fontWeight: "700", color: isSelectedDate ? "white" : pubColors.text || "white", mb: 0.5, children: dayNum }), _jsx(Text, { fontSize: "10px", fontWeight: "400", color: isSelectedDate ? "white" : pubColors.text || "white", style: {
                                                                opacity: isTodayDate || isSelectedDate ? 1 : 0.6,
                                                            }, textTransform: "uppercase", letterSpacing: "0.5px", children: isTodayDate ? "Today" : month })] }), isTodayDate && (_jsx(Box, { w: "1px", h: "70px", bg: pubColors.card_border || "#353535", ml: 2, zIndex: 10 }))] }, index));
                                    }) }) })] }), _jsxs(Flex, { justify: "center", align: "center", mt: 3, cursor: "pointer", onClick: () => setShowCalendar(!showCalendar), color: pubColors.primary || "#A383FF", children: [_jsx(Text, { fontSize: "xs", fontWeight: "400", mr: 1, color: pubColors.primary || "#A383FF", children: "View All Dates" }), _jsx(ChevronDownIcon, { transform: showCalendar ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" })] }), _jsx(Collapse, { in: showCalendar, animateOpacity: true, children: _jsx(Box, { mt: 1, borderTop: `1px solid ${pubColors.card_border || "#3A3A3A"}`, maxW: "500px", mx: "auto", children: _jsx(Box, { sx: {
                                    ".react-datepicker__day": {
                                        fontSize: "12px !important",
                                    },
                                }, children: _jsx(DateSelectionCalendar, { selectedDate: selectedDate, onDateChange: handleCalendarDateChange, hideBorder: true, bg: "transparent", pubColors: pubColors }) }) }) })] }), _jsx(Container, { ref: eventsContainerRef, w: ["100%", "100%", "100%", "400px"], h: "95vh", p: "0", pt: "133px", my: 0, css: {
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                }, mx: "auto", children: isEventsLoading || pubConfigLoading ? (_jsx(Flex, { w: "100%", h: "75vh", alignItems: "center", justifyContent: "center", children: _jsx(Spinner, { color: pubColors.primary || "white" }) })) : filteredEvents.length && pubColors ? (filteredEvents.length > 1 && !idParam ? (_jsx(Flex, { flexDir: "column", w: "100%", pb: 20, px: 0, mt: -4, gap: 4, children: filteredEvents.map((event, index) => (_jsx(EventDetailCard, { event: event, pubColors: pubColors, pub_id: pub_id }, event.uuid || index))) })) : (_jsx(Box, { position: "relative", mt: -5, children: _jsx(EventDetails2, { event: idParam
                            ? filteredEvents.find((event) => event.uuid === idParam) || filteredEvents[0]
                            : filteredEvents[0], pubConfig: pubConfig === null || pubConfig === void 0 ? void 0 : pubConfig.data, appContext: appContext }, idParam ? idParam : filteredEvents[0].uuid) }))) : (_jsx(Box, { pt: 28, px: 4, children: _jsxs(Flex, { flexDir: "column", gap: 4, align: "center", textAlign: "center", children: [_jsx(Image, { src: `${IMG_URL}/icons/eyes.svg`, alt: "Eyes icon", mb: 6 }), _jsx(Text, { fontSize: "xl", fontWeight: "400", color: pubColors.text || "white", maxW: "250px", children: "Bookings not opened for this day yet." }), _jsx(Text, { fontSize: "xs", fontWeight: "400", color: pubColors.text || "white", opacity: 0.8, children: "Explore for other dates" }), (((_d = (_c = events === null || events === void 0 ? void 0 : events.data) === null || _c === void 0 ? void 0 : _c.previousEventDates) === null || _d === void 0 ? void 0 : _d.length) > 0 ||
                                ((_e = events === null || events === void 0 ? void 0 : events.data) === null || _e === void 0 ? void 0 : _e.nextEventDate)) && (_jsxs(Flex, { gap: 3, mt: 1, flexWrap: "wrap", justify: "center", maxW: "100%", children: [(_g = (((_f = events === null || events === void 0 ? void 0 : events.data) === null || _f === void 0 ? void 0 : _f.previousEventDates) || [])) === null || _g === void 0 ? void 0 : _g.map((dateStr) => {
                                        const date = parseDateFromString(dateStr);
                                        if (!date)
                                            return null;
                                        return (_jsxs(Box, { w: "56px", minW: "56px", h: "66px", borderRadius: "8px", bg: pubColors.card_bg || "#202020", border: `1px solid ${pubColors.card_border || "#2B2B2B"}`, display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", onClick: () => handleDateClick(date), _hover: { bg: pubColors.card_bg || "#2a2a2a" }, children: [_jsx(Text, { fontSize: "10px", fontWeight: "400", color: pubColors.text || "white", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", children: formatDayName(date) }), _jsx(Text, { fontSize: "sm", fontWeight: "700", color: pubColors.text || "white", mb: 0.5, children: date.getDate() }), _jsx(Text, { fontSize: "10px", fontWeight: "400", color: pubColors.text || "#9D9D9D", style: { opacity: 0.6 }, textTransform: "uppercase", letterSpacing: "0.5px", children: formatMonth(date) })] }, dateStr));
                                    }), (() => {
                                        var _a;
                                        const nextDateStr = (_a = events === null || events === void 0 ? void 0 : events.data) === null || _a === void 0 ? void 0 : _a.nextEventDate;
                                        const nextDate = nextDateStr
                                            ? parseDateFromString(nextDateStr)
                                            : null;
                                        if (!nextDate)
                                            return null;
                                        return (_jsxs(Box, { w: "56px", minW: "56px", h: "66px", borderRadius: "8px", bg: pubColors.card_bg || "#202020", border: `1px solid ${pubColors.card_border || "#2B2B2B"}`, display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", onClick: () => handleDateClick(nextDate), _hover: { bg: pubColors.card_bg || "#2a2a2a" }, children: [_jsx(Text, { fontSize: "10px", fontWeight: "400", color: pubColors.text || "white", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", children: formatDayName(nextDate) }), _jsx(Text, { fontSize: "sm", fontWeight: "700", color: pubColors.text || "white", mb: 0.5, children: nextDate.getDate() }), _jsx(Text, { fontSize: "10px", fontWeight: "400", color: pubColors.text || "#9D9D9D", style: { opacity: 0.6 }, textTransform: "uppercase", letterSpacing: "0.5px", children: formatMonth(nextDate) })] }));
                                    })()] }))] }) })) })] }));
}
